import { useEffect, useMemo, useState } from 'react';
import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  getDaysInMonth,
  isSameDay,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths
} from 'date-fns';
import { AppShell, EmptyState, PeriodPicker, StatCard } from '../../ui';
import { useTransacoes } from '../../servicos/transacoes/useTransacoes';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { useFiltroContexto } from '../../contextos/FiltroContexto';
import { useCategoriaContexto } from '../../contextos/CategoriaContexto';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
import { ehMovimentoInterno, valorParaTexto } from '../../utils/Utils';
import { montarDelta, projetarFimDeMes, somarPorTipo, tomDelta } from './calculos';
import FluxoCaixaCard from './FluxoCaixaCard';
import CategoriasCard from './CategoriasCard';
import InsightsCard from './InsightsCard';
import MetasCard from './MetasCard';
import UltimasCard from './UltimasCard';
import styles from './Dashboard.module.css';

/**
 * Dashboard: responde "como estou este período? pra onde foi o dinheiro?
 * o que vem aí?" — comparando sempre com o período anterior de mesma duração.
 */
export default function Dashboard() {
  const { filtros, setFiltros } = useFiltroContexto();
  const { categorias } = useCategoriaContexto();
  const { atualizar } = useAtualizacaoContexto();
  const { fetchTransacoes } = useTransacoes(false);

  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [transacoesAnteriores, setTransacoesAnteriores] = useState<Transacao[]>([]);
  const [carregou, setCarregou] = useState(false);

  const inicio = filtros.dashboard.dataInicial;
  const fim = filtros.dashboard.dataFinal;

  useEffect(() => {
    let ativo = true;

    const buscar = async () => {
      // Período anterior de mesma duração; quando o range é um mês-calendário
      // cheio, compara com o mês-calendário anterior.
      const ehMesCheio = isSameDay(inicio, startOfMonth(inicio)) && isSameDay(fim, endOfMonth(inicio));
      let anteriorInicio: Date;
      let anteriorFim: Date;

      if (ehMesCheio) {
        const mesAnterior = subMonths(inicio, 1);
        anteriorInicio = startOfMonth(mesAnterior);
        anteriorFim = endOfDay(endOfMonth(mesAnterior));
      } else {
        const dias = differenceInCalendarDays(fim, inicio) + 1;
        anteriorFim = endOfDay(subDays(inicio, 1));
        anteriorInicio = startOfDay(subDays(inicio, dias));
      }

      const [atual, anterior] = await Promise.all([
        fetchTransacoes({ dataInicial: inicio, dataFinal: fim }),
        fetchTransacoes({ dataInicial: anteriorInicio, dataFinal: anteriorFim })
      ]);

      if (!ativo) return;
      // Movimentos internos ficam fora de TODAS as análises do dashboard:
      // pagamento de fatura é quitação (as compras já contam uma a uma) e
      // transferência entre contas é o mesmo dinheiro mudando de bolso.
      setTransacoes((atual ?? []).filter((t) => !ehMovimentoInterno(t)));
      setTransacoesAnteriores((anterior ?? []).filter((t) => !ehMovimentoInterno(t)));
      setCarregou(true);
    };

    buscar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.dashboard, atualizar]);

  const resumo = useMemo(
    () => ({
      receitas: somarPorTipo(transacoes, 'R'),
      despesas: somarPorTipo(transacoes, 'D'),
      receitasAnteriores: somarPorTipo(transacoesAnteriores, 'R'),
      despesasAnteriores: somarPorTipo(transacoesAnteriores, 'D')
    }),
    [transacoes, transacoesAnteriores]
  );

  const saldo = resumo.receitas - resumo.despesas;

  // Quarto card: projeção run-rate quando o range é o mês corrente; senão, taxa de poupança.
  const hoje = new Date();
  const ehMesCorrente = isSameDay(inicio, startOfMonth(hoje)) && isSameDay(fim, endOfMonth(hoje));

  let quartoCard;
  if (ehMesCorrente) {
    const diasDoMes = getDaysInMonth(hoje);
    const diasDecorridos = hoje.getDate();
    const projecao = projetarFimDeMes(resumo.despesas, diasDecorridos, diasDoMes);
    quartoCard = (
      <StatCard
        rotulo="Projeção do mês"
        valor={valorParaTexto(projecao, true)}
        icone="query_stats"
        tom="primario"
        dica="gasto total no ritmo atual"
      />
    );
  } else {
    const taxa =
      resumo.receitas > 0 ? Math.round(((resumo.receitas - resumo.despesas) / resumo.receitas) * 100) : undefined;
    quartoCard = (
      <StatCard
        rotulo="Taxa de poupança"
        valor={taxa === undefined ? '—' : `${taxa}%`}
        icone="savings"
        tom={taxa === undefined ? 'neutro' : taxa >= 0 ? 'positivo' : 'negativo'}
        dica="do que entrou, quanto sobrou"
      />
    );
  }

  const semDados = carregou && transacoes.length === 0;

  return (
    <AppShell
      titulo="Dashboard"
      subtitulo="Seu dinheiro neste período, num relance"
      acoes={
        <PeriodPicker
          inicio={inicio}
          fim={fim}
          onChange={(novoInicio, novoFim) =>
            setFiltros((atuais) => ({ ...atuais, dashboard: { dataInicial: novoInicio, dataFinal: novoFim } }))
          }
        />
      }
    >
      {!carregou && (
        <div className={styles.carregando}>
          <div className={styles.esqueleto} />
          <div className={styles.esqueleto} />
          <div className={styles.esqueleto} />
        </div>
      )}

      {semDados && (
        <EmptyState
          icone="query_stats"
          titulo="Nenhuma movimentação neste período"
          descricao='Registre a primeira pelo botão "Nova transação" para ver o resumo do período.'
        />
      )}

      {carregou && !semDados && (
        <div className={styles.conteudo}>
          <div className={styles.estatisticas}>
            <StatCard
              rotulo="Receitas"
              valor={valorParaTexto(resumo.receitas, true)}
              icone="trending_up"
              delta={montarDelta(resumo.receitas, resumo.receitasAnteriores)}
              deltaTom={tomDelta(resumo.receitas, resumo.receitasAnteriores, true)}
              dica="vs período anterior"
            />
            <StatCard
              rotulo="Despesas"
              valor={valorParaTexto(resumo.despesas, true)}
              icone="trending_down"
              delta={montarDelta(resumo.despesas, resumo.despesasAnteriores)}
              deltaTom={tomDelta(resumo.despesas, resumo.despesasAnteriores, false)}
              dica="vs período anterior"
            />
            <StatCard
              rotulo="Saldo do período"
              valor={valorParaTexto(saldo, true)}
              icone="account_balance_wallet"
              tom={saldo >= 0 ? 'positivo' : 'negativo'}
              dica="receitas − despesas"
            />
            {quartoCard}
          </div>

          <FluxoCaixaCard transacoes={transacoes} inicio={inicio} fim={fim} />

          <div className={styles.linhaLarga}>
            <CategoriasCard transacoes={transacoes} categorias={categorias} inicio={inicio} fim={fim} />
            <InsightsCard
              transacoes={transacoes}
              transacoesAnteriores={transacoesAnteriores}
              categorias={categorias}
              inicio={inicio}
              fim={fim}
            />
          </div>

          <div className={styles.linhaIgual}>
            <MetasCard transacoes={transacoes} categorias={categorias} />
            <UltimasCard transacoes={transacoes} categorias={categorias} />
          </div>
        </div>
      )}
    </AppShell>
  );
}
